import _ from "lodash";

/**
 * Initializes the manual selection tool in WPD.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	// cannot pick and choose functions to override due to how it was written
	// completely overwrite the manual selection tool into a js class
	wpd.ManualSelectionTool = class {
		constructor(axis, dataset) {
			this.axis = axis;
			this.dataset = dataset;
			this.#$button = wpdDocument.getElementById("manual-select-button");
		}

		static #snapToThreshold = 50;

		#$button;
		#snapToValue = null;
		#snapToDirection = null;

		#tupleHasDuplicateForActivePointGroup(activePointGroup) {
			const axisType = wpd.custom.identifyAxis(this.axis);
			const mainTags = wpd.alignAxes.getGraphAxisTags(this.axis);
			const mainMatchTags =
				axisType === "xy"
					? [mainTags.xMainTag, mainTags.yMainTag]
					: [mainTags.barMainTag];
			const tolerance = wpd.pointGroups?.constructor?.minTolerance ?? 2;

			const isMainDataPoint = (dataPoint) => {
				const tags = this.dataset.getTagsForDataPoint(dataPoint) || [];
				return mainMatchTags.length === 1
					? tags.some((t) => t && t.uuid === mainMatchTags[0].uuid)
					: mainMatchTags.every((mt) =>
							tags.some((t) => t && t.uuid === mt.uuid),
					  );
			};

			return this.dataset.getAllPixels().some((dataPoint, idx) => {
				if (!isMainDataPoint(dataPoint)) return false;
				if (
					Math.abs(dataPoint[this.#snapToDirection] - this.#snapToValue) >
					tolerance
				)
					return false;

				const tupleIndex = this.dataset.getTupleIndex(idx);
				if (tupleIndex <= -1) return false;
				const tuple = this.dataset.getTuple(tupleIndex) || [];

				return tuple.some((pointIndex) => {
					if (pointIndex == null) return false;
					const dp = this.dataset.getPixel(pointIndex);
					if (
						dp?.metadata?.tagGroupAxisDirection !==
						activePointGroup.axisDirection
					)
						return false;
					const dpTags = this.dataset.getTagsForDataPoint(dp) || [];
					return dpTags.some(
						(tag) => tag && tag.uuid === activePointGroup.tag.uuid,
					);
				});
			});
		}

		#tupleHasDuplicateForActivePointGroupV2(activePointGroup) {
			const tolerance = wpd.pointGroups?.constructor?.minTolerance ?? 2;

			if (dataEntryVM.pointGroupProximityFixes) {
				// find the closest center data point within tolerance
				const closestCenter = this.dataset
					.getAllPixels()
					.reduce((closest, dataPoint) => {
						if (!wpd.pointGroups.isCenterDataPoint(this.axis, dataPoint)) {
							return closest;
						}
						const distance = Math.abs(
							dataPoint[this.#snapToDirection] - this.#snapToValue,
						);
						if (distance > tolerance) {
							return closest;
						}
						if (
							!closest ||
							distance <
								Math.abs(closest[this.#snapToDirection] - this.#snapToValue)
						) {
							return dataPoint;
						}
						return closest;
					}, null);

				if (!closestCenter) return false;

				const tupleDataPointIndexes = wpd.pointGroups.getTupleDataPointIndexes(
					this.dataset,
					closestCenter,
					activePointGroup.axisDirection,
				);

				return tupleDataPointIndexes.some((index) => {
					if (index == null) return false;
					const dp = this.dataset.getPixel(index);
					if (
						dp?.metadata?.pointGroupAxisDirection !==
						activePointGroup.axisDirection
					) {
						return false;
					}
					const dpTags = this.dataset.getTagsForDataPoint(dp) || [];
					return dpTags.some(
						(tag) => tag && tag.uuid === activePointGroup.tag.uuid,
					);
				});
			}

			return this.dataset.getAllPixels().some((dataPoint) => {
				if (!wpd.pointGroups.isCenterDataPoint(this.axis, dataPoint)) {
					return false;
				}

				if (
					Math.abs(dataPoint[this.#snapToDirection] - this.#snapToValue) >
					tolerance
				)
					return false;

				const tupleDataPointIndexes = wpd.pointGroups.getTupleDataPointIndexes(
					this.dataset,
					dataPoint,
					activePointGroup.axisDirection,
				);

				return tupleDataPointIndexes.some((index) => {
					if (index == null) return false;
					const dp = this.dataset.getPixel(index);
					if (
						dp?.metadata?.pointGroupAxisDirection !==
						activePointGroup.axisDirection
					) {
						return false;
					}
					const dpTags = this.dataset.getTagsForDataPoint(dp) || [];
					return dpTags.some(
						(tag) => tag && tag.uuid === activePointGroup.tag.uuid,
					);
				});
			});
		}

		#findActivePointGroupIndex(axisDirection, activePointGroup) {
			const pointGroups = this.dataset.getMetadata()?.pointGroups;
			if (!pointGroups || !activePointGroup) return -1;
			return pointGroups.findIndex(
				(pointGroup) =>
					pointGroup.tagUuid === activePointGroup.tag.uuid &&
					pointGroup.axis === axisDirection,
			);
		}

		#findNearestSnapToValue(snapToDirection, currentPosition) {
			const metadata = this.axis.getMetadata();

			if (metadata.axesLabelData) {
				// get main tag in given point group settings direction
				const tagMatchFilter = [];

				// get main tags
				const mainTags = wpd.alignAxes.getGraphAxisTags(this.axis);

				const axisType = wpd.custom.identifyAxis(this.axis);
				if (axisType === "xy") {
					tagMatchFilter.push(mainTags.xMainTag, mainTags.yMainTag);
				} else if (axisType === "bar") {
					tagMatchFilter.push(mainTags.barMainTag);
				}

				// find the nearest point in the given direction and position
				const nearestDataPoint = this.dataset.findNearestPixelByAxisDirection(
					snapToDirection,
					currentPosition,
					wpd.graphicsWidget.toScreenPixel(
						wpd.ManualSelectionTool.#snapToThreshold,
					),
					tagMatchFilter,
				);

				if (nearestDataPoint) {
					return nearestDataPoint[snapToDirection];
				}
			}

			return null;
		}

		onAttach() {
			// clear selected data points
			// prior data point selection is ignored by this tool
			this.dataset.unselectAll();

			// close hover
			wpd.events.dispatch("wpd.dataset.point.closeHover");

			this.#$button.classList.add("pressed-button");
			wpd.graphicsWidget.setRepainter(
				new wpd.DataPointsRepainter(this.axis, this.dataset),
			);

			if (wpd.tagGroups.activeTagGroup) {
				// display tag group ui
				wpd.tagGroups.showControls();
			}
		}

		onMouseMove(ev, pos, imagePos) {
			// if axis is an xy graph, check if cursor is over the calibrated area
			if (wpd.pointGroups.isSupportedAxis(this.axis)) {
				const activePointGroup = wpd.tree.getActivePointGroup();

				if (activePointGroup && this.axis.calibration) {
					// force repaint
					wpd.graphicsWidget.forceHandlerRepaint();

					// draw extended crosshair if enabled
					if (
						dataEntryVM.pointGroupOverhaul &&
						wpd.graphicsWidget.extendedCrosshair
					) {
						wpd.graphicsWidget.drawExtendedCrosshair(imagePos.x, imagePos.y);
					}

					// get corners of the calibrated area
					let { topLeft, bottomRight } = wpd.alignAxes.getAxisCorners(
						this.axis,
					);

					// pad calibrated area for an expanded target area
					// some graphs do not have tick marks near the edges
					const padding = 500;
					topLeft.px -= padding;
					topLeft.py -= padding;
					bottomRight.px += padding;
					bottomRight.py += padding;

					// check if cursor is within the calibrated area
					if (
						imagePos.x >= topLeft.px &&
						imagePos.x <= bottomRight.px &&
						imagePos.y >= topLeft.py &&
						imagePos.y <= bottomRight.py
					) {
						const currentRotation = wpd.graphicsWidget.getRotation();
						const swapAxes = currentRotation && currentRotation % 180 !== 0;
						const originalAxisDirection = activePointGroup.axisDirection;
						let axisDirection = swapAxes
							? originalAxisDirection === "x"
								? "y"
								: "x"
							: originalAxisDirection;
						let snapToDirection = axisDirection === "x" ? "y" : "x";

						const initialRotation = this.axis.getMetadata().initialRotation;
						const rotationDifference = Math.abs(
							currentRotation - initialRotation,
						);
						const swapXY =
							rotationDifference > 0 && rotationDifference % 180 !== 0;

						if (swapXY) {
							snapToDirection = snapToDirection === "x" ? "y" : "x";
							axisDirection = axisDirection === "x" ? "y" : "x";
						}

						this.#snapToValue = this.#findNearestSnapToValue(
							snapToDirection,
							imagePos[snapToDirection],
						);
						this.#snapToDirection = null;

						// snap extended crosshair to snap-to value if enabled
						if (
							dataEntryVM.pointGroupOverhaul &&
							wpd.graphicsWidget.extendedCrosshair
						) {
							wpd.graphicsWidget.drawExtendedCrosshair(
								axisDirection === "x" ? imagePos.x : this.#snapToValue,
								axisDirection === "x" ? this.#snapToValue : imagePos.y,
							);
						}

						if (this.#snapToValue) {
							this.#snapToDirection = snapToDirection;

							let fillStyle = wpd.Colors.goodHoverA;
							if (dataEntryVM.pointGroupColors) {
								const pointGroupColors =
									wpd.pointGroups.calculatePointGroupColors(this.axis, true);

								let xPointGroupIndexColorMap = swapAxes
									? pointGroupColors.y
									: pointGroupColors.x;
								let yPointGroupIndexColorMap = swapAxes
									? pointGroupColors.x
									: pointGroupColors.y;

								if (swapXY) {
									[xPointGroupIndexColorMap, yPointGroupIndexColorMap] = [
										yPointGroupIndexColorMap,
										xPointGroupIndexColorMap,
									];
								}

								const colorMap =
									axisDirection === "x"
										? xPointGroupIndexColorMap
										: yPointGroupIndexColorMap;
								const pointGroupIndex = this.#findActivePointGroupIndex(
									originalAxisDirection,
									activePointGroup,
								);

								fillStyle =
									colorMap.get(pointGroupIndex) ?? wpd.Colors.goodHoverA;
							}

							// draw the snap-to data point
							wpd.graphicsHelper.drawPoint(
								{
									[axisDirection]: imagePos[axisDirection],
									[snapToDirection]: this.#snapToValue,
								},
								fillStyle,
								activePointGroup.tag.name,
							);
						}
					}
				}
			}
		}

		onMouseClick(ev, pos, imagePos) {
			const addPixelArgs = [imagePos.x, imagePos.y];
			const activePointGroup = wpd.tree.getActivePointGroup();

			// With the flag on, limit adding point group points around a central tendency point, and prevent adding
			// duplicate point group points on the same tuple + axis.
			if (dataEntryVM.pointGroupColors) {
				if (activePointGroup) {
					if (this.#snapToValue && this.#snapToDirection) {
						// Prevent adding if a point with the same tag already exists in the SAME tuple and axis
						if (
							dataEntryVM.pointGroupOverhaul
								? this.#tupleHasDuplicateForActivePointGroupV2(activePointGroup)
								: this.#tupleHasDuplicateForActivePointGroup(activePointGroup)
						) {
							dataEntryVM.showNotification({
								message: `The selected tuple already contains a data point for the "${
									activePointGroup.tag.name
								}" tag on the ${activePointGroup.axisDirection.toUpperCase()} axis`,
								status: "info",
							});
							return;
						}
						// add the snap-to value to the pixel
						if (this.#snapToDirection === "x") {
							addPixelArgs[0] = this.#snapToValue;
						} else if (this.#snapToDirection === "y") {
							addPixelArgs[1] = this.#snapToValue;
						}

						// reset snap-to values
						this.#snapToValue = null;
						this.#snapToDirection = null;
					} else {
						dataEntryVM.showNotification({
							message: `The point group"${activePointGroup.tag.name}" must be attached to a central tendency point`,
							status: "info",
						});
						return;
					}
				}
			} else {
				if (activePointGroup && this.#snapToValue && this.#snapToDirection) {
					// add the snap-to value to the pixel
					if (this.#snapToDirection === "x") {
						addPixelArgs[0] = this.#snapToValue;
					} else if (this.#snapToDirection === "y") {
						addPixelArgs[1] = this.#snapToValue;
					}
					// reset snap-to values
					this.#snapToValue = null;
					this.#snapToDirection = null;
				}
			}

			// add the pixel to the dataset
			const index = this.dataset.addPixel(...addPixelArgs);

			// draw the point
			wpd.graphicsHelper.drawPoint(imagePos, wpd.Colors.active);

			wpd.graphicsWidget.updateZoomOnEvent(ev);
			wpd.dataPointCounter.setCount(this.dataset.getCount());

			// dispatch point add event
			wpd.events.dispatch("wpd.dataset.point.add", {
				axes: this.axis,
				dataset: this.dataset,
				index: index,
			});
		}

		onRemove() {
			// do not clear selected data points to allow adjustment tool to
			// immediately edit the most recently added data point

			wpd.graphicsWidget.forceHandlerRepaint();

			this.#$button.classList.remove("pressed-button");
		}

		onKeyDown(event) {
			const selectedIndexes = this.dataset.getSelectedPixels();

			if (wpd.acquireData.isToolSwitchKey(event.keyCode)) {
				wpd.acquireData.switchToolOnKeyPress(
					String.fromCharCode(event.keyCode).toLowerCase(),
				);
				return;
			}

			if (selectedIndexes.length > 0) {
				const firstDataPoint = this.dataset.getPixel(selectedIndexes[0]);
				const stepSize = 0.5 / wpd.graphicsWidget.getZoomRatio();

				// rotate to current rotation
				const currentRotation = wpd.graphicsWidget.getRotation();
				let { x, y } = wpd.graphicsWidget.getRotatedCoordinates(
					0,
					currentRotation,
					firstDataPoint.x,
					firstDataPoint.y,
				);

				if (wpd.keyCodes.isUp(event.keyCode)) {
					y = y - stepSize;
				} else if (wpd.keyCodes.isDown(event.keyCode)) {
					y = y + stepSize;
				} else if (wpd.keyCodes.isLeft(event.keyCode)) {
					x = x - stepSize;
				} else if (wpd.keyCodes.isRight(event.keyCode)) {
					x = x + stepSize;
				} else if (wpd.keyCodes.isComma(event.keyCode)) {
					return;
				} else if (wpd.keyCodes.isPeriod(event.keyCode)) {
					return;
				} else {
					return;
				}

				// rotate back to original rotation
				({ x, y } = wpd.graphicsWidget.getRotatedCoordinates(
					currentRotation,
					0,
					x,
					y,
				));

				_.each(selectedIndexes, (index) =>
					this.dataset.setPixelAt(index, x, y),
				);
				wpd.graphicsWidget.resetData();
				wpd.graphicsWidget.forceHandlerRepaint();
				wpd.graphicsWidget.updateZoomToImagePosn(
					firstDataPoint.x,
					firstDataPoint.y,
				);
			}

			event.preventDefault();
		}
	};
}

export { init };
